import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { colors, fonts } from "../theme";
import { categories } from "../data";
import { Eyebrow, Btn, ProductImage } from "../ui";
import ProductEnquiryModal from "../ProductEnquiryModal";
import { canSubmitDirectly } from "../enquiry";
import NotFound from "./NotFound";
import FaqSection from "../FaqSection";
import Seo from "../../seo/Seo";
import { categoryMeta } from "../../seo/pageMeta";
import { categoryFaqs } from "../../seo/faqs";
import { hasProductPage, productPath } from "../../seo/productMeta";
import { absUrl } from "../../seo/site";
import {
  webPageSchema,
  breadcrumbSchema,
  itemListSchema,
  faqSchema,
} from "../../seo/schema";

const wrap = { maxWidth: "1180px", margin: "0 auto" };

// The product grid is minmax(210px,1fr) inside a 1124px content column, so a
// desktop row holds five cards and the first row sits well above the fold —
// its images are the LCP candidate on this page. The first four are loaded
// eagerly at high priority; the rest stay lazy.
const EAGER_CARDS = 4;

function ItemCard({ item, onEnquire, priority, href }) {
  // The card title becomes a link only when the grade has its own page. That
  // link is also how the prerenderer discovers the product route, so a product
  // without written copy is never linked, never crawled and never indexed.
  //
  // As a link it was an 18px-tall target, so it gets blocked out to 44px.
  // The non-link branch stays a plain text run — nothing to hit.
  const Title = href ? (
    <Link
      to={href}
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: "44px",
        fontSize: "var(--fs-body)",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {item.name}
    </Link>
  ) : (
    <div style={{ fontSize: "var(--fs-body)", fontWeight: 600 }}>{item.name}</div>
  );

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: "4px",
        background: colors.white,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ProductImage
        src={item.image}
        alt={item.alt || item.name}
        tag={item.tag}
        ratio="4/3"
        priority={priority}
      />
      <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", flex: 1 }}>
        {Title}
        {/* 12.5px/1.45 failed both the minimum size and the line-height floor,
            and muted2 (#8A8175) is 3.83:1 on white — under 4.5:1. muted keeps
            the same grey cast at 5.57:1. */}
        <div style={{ fontSize: "var(--fs-base)", color: colors.muted, marginTop: "4px", lineHeight: 1.55, flex: 1 }}>
          {item.desc}
        </div>
        {href && (
          // The only route into the product page, and it was a 16px-tall
          // target. inline-flex + minHeight gives it a real box; the reduced
          // marginTop keeps the card's rhythm now that the link carries
          // its own height.
          <Link
            to={href}
            style={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: "44px",
              fontSize: "var(--fs-sm)",
              color: colors.brown,
              marginTop: "4px",
              cursor: "pointer",
            }}
          >
            Specifications &amp; uses →
          </Link>
        )}
        <button
          type="button"
          className="se-btn-dark"
          onClick={onEnquire}
          // 9px padding on a 12.5px label measured 34px — 10px under the
          // floor, repeated once per card. minHeight holds the 44px on its own
          // so a future padding tweak cannot shrink it back.
          style={{
            marginTop: "14px",
            minHeight: "46px",
            padding: "12px 14px",
            borderRadius: "2px",
            fontSize: "var(--fs-sm)",
            fontWeight: 500,
            cursor: "pointer",
            width: "100%",
          }}
        >
          {/* The popup this opens now offers a direct send as well as WhatsApp,
              so naming one channel would be a promise the popup doesn't keep.
              An unconfigured build really is WhatsApp-only, and says so. */}
          {canSubmitDirectly ? "Enquire" : "Enquire on WhatsApp"}
        </button>
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams();
  const cat = categories.find((c) => c.slug === slug);
  const [enquiryItem, setEnquiryItem] = useState(null);

  // An unknown slug is a dead URL, not a redirect target — rendering the 404
  // view keeps Apache's ErrorDocument and the client in agreement.
  if (!cat) return <NotFound />;

  const meta = categoryMeta(cat);
  const path = `/catalogue/${cat.slug}`;
  const faqs = categoryFaqs(cat.slug);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Catalogue", path: "/catalogue" },
    { name: cat.name, path },
  ];

  return (
    <main
      className="se-page"
      style={{
        ...wrap,
        padding: "clamp(36px,8vw,60px) var(--sp-gutter) clamp(48px,11vw,84px)",
      }}
    >
      <Seo
        title={meta.title}
        description={meta.description}
        path={path}
        image={cat.image}
        imageAlt={`${cat.name} stocked by Sigma Enterprises, New Delhi`}
        schema={[
          webPageSchema({
            path,
            name: meta.title,
            description: meta.description,
            type: "CollectionPage",
            primaryImage: cat.image,
            breadcrumbId: `${absUrl(path)}#breadcrumb`,
          }),
          breadcrumbSchema(crumbs, path),
          itemListSchema({
            path,
            name: cat.name,
            items: cat.items.map((item) => ({
              name: item.name,
              desc: item.desc,
              image: item.image,
              category: cat.name,
            })),
          }),
          // Only emitted when this category actually has visible Q&A on the
          // page — FAQPage markup without matching visible text is a policy
          // violation, not a shortcut.
          faqs.length ? faqSchema(faqs, path) : null,
        ]}
      />

      {/* Visible breadcrumb, matching the BreadcrumbList markup above. Google
          only shows breadcrumb rich results when the trail is on the page.
          .se-crumbs lets the row wrap below 700px and gives each crumb a 44px
          target instead of a 16px text run. */}
      <nav
        className="se-crumbs"
        aria-label="Breadcrumb"
        style={{ fontSize: "var(--fs-meta)", color: colors.muted2, marginBottom: "18px" }}
      >
        <Link to="/" style={{ color: colors.brown }}>Home</Link>
        <span style={{ margin: "0 8px" }}>/</span>
        <Link to="/catalogue" style={{ color: colors.brown }}>Catalogue</Link>
        <span style={{ margin: "0 8px" }}>/</span>
        <span aria-current="page">{cat.name}</span>
      </nav>

      <Eyebrow>{cat.count} grades</Eyebrow>
      <h1
        style={{
          fontFamily: fonts.serif,
          fontSize: "var(--fs-h1)",
          fontWeight: 500,
          margin: "12px 0 10px",
          maxWidth: "22ch",
        }}
      >
        {meta.h1}
      </h1>
      <p style={{ fontSize: "16.5px", color: colors.muted, maxWidth: "62ch", margin: "0 0 40px" }}>
        {meta.intro}
      </p>

      {/* min(210px,100%) stops the track floor from ever exceeding the
          container — a bare 210px minimum keeps that width inside a 280px
          phone column and pushes the whole document sideways. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(210px,100%),1fr))", gap: "18px" }}>
        {cat.items.map((item, i) => (
          <ItemCard
            key={item.name}
            item={item}
            priority={i < EAGER_CARDS}
            href={
              hasProductPage(cat.slug, item.slug)
                ? productPath(cat.slug, item.slug)
                : null
            }
            onEnquire={() => setEnquiryItem(item)}
          />
        ))}
      </div>

      <FaqSection
        faqs={faqs}
        eyebrow="Common questions"
        title={`Choosing ${cat.name.toLowerCase()}`}
      />

      {/* .se-cta-band stretches the two buttons to full width once they stack
          below 560px; .se-panel trims the 34px/32px padding, which was adding
          another 64px inside gutters that are already 40px. */}
      <div
        className="se-cta-band se-panel"
        style={{
          marginTop: "56px",
          padding: "34px 32px",
          border: `1px solid ${colors.border}`,
          borderRadius: "4px",
          background: colors.panel,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "24px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: "16px", color: colors.ink3, maxWidth: "52ch" }}>
          Need a specific grade or grammage from {cat.name}? Send us your requirement for availability and a trade quote.
        </div>
        {/* No padding or nowrap overrides: Btn is fluid now, the fixed 13px
            vertical dropped it under the 44px floor, and nowrap fought the
            full-width stack .se-cta-band gives these two on a phone. */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <Btn variant="ghost" to="/catalogue">
            ← All categories
          </Btn>
          <Btn variant="dark" to="/contact">
            Enquire now →
          </Btn>
        </div>
      </div>

      {enquiryItem && (
        <ProductEnquiryModal
          item={enquiryItem}
          categoryName={cat.name}
          onClose={() => setEnquiryItem(null)}
        />
      )}
    </main>
  );
}
